import React, { useState } from 'react';
import { ShieldCheck, Home, Coins, ArrowRight, CheckCircle, Loader2, Send, Building2, MapPin, Star, Mail, Phone } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Rent: React.FC = () => {
  const { t } = useLanguage();

  // Form State
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
	telephone: '',
    subject: 'Verhuuraanvraag Chalet Inn',
    message: '',
  });

  const API_BASE = `${import.meta.env.BASE_URL}api`;

  const [isSending, setIsSending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic Validation
    if (!formData.firstname || !formData.lastname || !formData.email || !formData.message) {
      setError('Vul alstublieft alle verplichte velden in.');
      return;
    }

    setError('');
    setIsSending(true);

    try {
      const res = await fetch(`${API_BASE}/contact.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const raw = await res.text();
      let data: any = {};
      try {
        data = JSON.parse(raw);
      } catch {
        // keep data as {}
      }

      if (!res.ok || !data.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      setIsSuccess(true);
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        telephone: '',
        subject: 'Verhuuraanvraag Chalet Inn',
        message: '',
      });
    } catch (err) {
      setError('Er is iets misgegaan. Probeer het later opnieuw.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-50 animate-in fade-in duration-500">
      {/* Header */}
      <div className="bg-brand-900 text-brand-50 py-24 px-4 relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center">
           <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">{t('rent.title')}</h1>
           <p className="text-brand-200 text-lg max-w-2xl mx-auto">
             {t('rent.subtitle')}
           </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 mb-20 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Benefits Column */}
            <div className="lg:col-span-1">
                <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-brand-100 h-full">
                    <h3 className="text-2xl font-serif font-bold text-brand-900 mb-8">{t('rent.benefits.title')}</h3>
                    
                    <div className="space-y-8">
                        <div className="flex items-start gap-4 group">
                            <div className="p-4 bg-brand-50 rounded-2xl text-brand-900 shadow-sm group-hover:bg-brand-900 group-hover:text-white transition-all duration-300">
                                <Star size={24} />
                            </div>
                            <div>
                                <p className="font-bold text-stone-800 text-lg">{t('rent.benefits.1.title')}</p>
                                <p className="text-stone-500 text-sm mt-1 leading-relaxed">{t('rent.benefits.1.text')}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 group">
                            <div className="p-4 bg-brand-50 rounded-2xl text-brand-900 shadow-sm group-hover:bg-brand-900 group-hover:text-white transition-all duration-300">
                                <Coins size={24} />
                            </div>
                            <div>
                                <p className="font-bold text-stone-800 text-lg">{t('rent.benefits.2.title')}</p>
                                <p className="text-stone-500 text-sm mt-1 leading-relaxed">{t('rent.benefits.2.text')}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4 group">
                            <div className="p-4 bg-brand-50 rounded-2xl text-brand-900 shadow-sm group-hover:bg-brand-900 group-hover:text-white transition-all duration-300">
                                <ShieldCheck size={24} />
                            </div>
                            <div>
                                <p className="font-bold text-stone-800 text-lg">{t('rent.benefits.3.title')}</p>
                                <p className="text-stone-500 text-sm mt-1 leading-relaxed">{t('rent.benefits.3.text')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

          {/* Contact Form Container */}
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-lg border border-brand-100 lg:col-span-2 relative overflow-hidden">
            {isSuccess ? (
              <div className="flex flex-col items-center justify-center py-12 text-center animate-in zoom-in duration-500">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center text-green-600 mb-6 shadow-sm border border-green-100">
                  <CheckCircle size={40} />
                </div>
                <h3 className="text-3xl font-serif font-bold text-brand-900 mb-4">
                  Bericht Verzonden!
                </h3>
                <p className="text-stone-500 max-w-md mx-auto mb-8">
                  Bedankt voor je vraag. We nemen zo snel mogelijk contact met je op via het opgegeven
                  e-mailadres.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-serif font-bold text-brand-900 mb-6">
                  {t('rent.form.title')}
                </h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        {t('contact.form.firstname')} *
                      </label>
                      <input
                        type="text"
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleInputChange}
                        required
                        className="w-full p-3 bg-brand-50 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:outline-none transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        {t('contact.form.lastname')} *
                      </label>
                      <input
                        type="text"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleInputChange}
                        required
                        className="w-full p-3 bg-brand-50 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      {t('contact.form.phone')} *
                    </label>
                    <input
                      type="text"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 bg-brand-50 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:outline-none transition-all"
                    />
                  </div>
				  
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      {t('contact.form.email')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 bg-brand-50 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      {t('contact.form.message')} *
                    </label>
                    <textarea
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      className="w-full p-3 bg-brand-50 border border-brand-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:outline-none transition-all"
                    />
                  </div>

                  {error && <p className="text-red-500 text-sm font-medium">{error}</p>}

                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={isSending}
                      className="bg-brand-900 text-white px-10 py-4 rounded-xl font-bold flex items-center gap-3 hover:bg-black transition-all shadow-lg hover:shadow-xl transform active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isSending ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Bezig met verzenden...
                        </>
                      ) : (
                        <>
                          <Send size={18} /> {t('contact.form.send')}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};